/* Route reads overlap code loading without gating component rendering. The shared
 * server query adapter waits for the reads each rendered component actually consumes.
 * Failures stay in the owning query cache for each region to show and retry. */

import type { QueryClient } from "@tanstack/react-query";

import type { Project, ProjectId } from "./account";
import type { GetCallerAccessResponse } from "@sx/protocol/platform/auth/organization";
import type { ProjectAddress } from "./nav";
import { authQueryKeys } from "./query-keys";

/** Await a prefetch for whatever it resolves to: its data, or nothing because it failed. */
export const started = <T>(read: Promise<T>): Promise<T | undefined> =>
  read.catch(() => undefined);

/** Start existing owner reads together once the authorized project is known.
 * This is read-ahead in either environment; no project means no owner reads. */
export function prefetchForProject<Reads>(
  queryClient: QueryClient,
  address: ProjectAddress,
  reads: Promise<Reads>,
  start: (reads: Reads, projectId: ProjectId) => Promise<unknown>,
): void {
  void started(
    (async () => {
      const [projectId, module] = await Promise.all([
        addressedProjectId(queryClient, address),
        started(reads),
      ]);
      if (projectId !== undefined && module !== undefined) await start(module, projectId);
    })(),
  );
}

/** The project an address names in the portfolio this identity holds, never a disabled
 *  one: the one answer every loader gives to "which project is this address". */
export function projectAt(
  projects: readonly Project[] | undefined,
  address: ProjectAddress,
): Project | undefined {
  return projects?.find(
    (candidate) =>
      !candidate.disabled &&
      candidate.organizationSlug === address.org &&
      candidate.slug === address.project,
  );
}

/** The addressed project from the portfolio already in memory, without a request. Keyed
 *  as `projectsQuery` keys it; that module carries its door client, which a loader's
 *  first-paint half must not load. */
export function cachedProjectAt(
  queryClient: QueryClient,
  principalId: string | undefined,
  address: ProjectAddress,
): Project | undefined {
  if (principalId === undefined) return undefined;
  return projectAt(
    queryClient.getQueryData<GetCallerAccessResponse>(
      authQueryKeys.principal(principalId, "caller-access", "portfolio"),
    )?.portfolio?.projects,
    address,
  );
}

/** The addressed project, reading the portfolio when it is not here yet (on a cold load,
 *  the read `_console` already started). */
export async function addressedProject(
  queryClient: QueryClient,
  address: ProjectAddress,
): Promise<Project | undefined> {
  const [{ loadedSession }, { callerAccessQuery, callerPortfolio }] = await Promise.all([
    import("./session"),
    import("./account"),
  ]);
  const principalId = loadedSession(queryClient)?.principal.id;
  if (principalId === undefined) return undefined;
  const access = await started(queryClient.ensureQueryData(callerAccessQuery(principalId)));
  return access === undefined ? undefined : projectAt(callerPortfolio(access).projects, address);
}

/** Request routing needs an ID, not the whole portfolio. A live session already names
 * projects in its grants. Use that address while portfolio metadata loads in parallel;
 * the owner still authorizes every read. Once a portfolio exists, its disabled/absent
 * result wins, including after a membership change. Never manufacture a Project from
 * a grant: callers needing display metadata continue to use addressedProject. */
export async function addressedProjectId(
  queryClient: QueryClient,
  address: ProjectAddress,
): Promise<ProjectId | undefined> {
  const { loadedSession } = await import("./session");
  const session = loadedSession(queryClient);
  if (session == null) return undefined;
  const access = queryClient.getQueryData<GetCallerAccessResponse>(
    authQueryKeys.principal(session.principal.id, "caller-access", "portfolio"),
  );
  if (access !== undefined) return projectAt(access.portfolio?.projects, address)?.id;
  const grant = session.principal.grants.find(({ project }) =>
    project.org.slug === address.org && project.slug === address.project,
  );
  return grant?.project.id ?? (await addressedProject(queryClient, address))?.id;
}
